import { useEffect, useState } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/format";
import { openWhatsApp, buildLoyaltyRewardMessage } from "@/lib/notify";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Banknote, ShoppingBag, Scale, Clock, CheckCircle2, AlertCircle, Plus, Users, Wallet, Gift, MessageCircle,
} from "lucide-react";

const KPI = ({ label, value, icon: Icon, color, testid }) => (
  <Card data-testid={testid} className={`p-4 border-0 text-white ${color} shadow-lg`}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-white/80 text-xs sm:text-sm font-medium leading-snug">{label}</p>
      <div className="h-8 w-8 rounded-lg bg-white/15 grid place-items-center shrink-0">
        <Icon size={18} className="text-white" />
      </div>
    </div>
    <p className="font-display text-xl sm:text-2xl font-bold mt-2 whitespace-nowrap">{value}</p>
  </Card>
);

export default function Dashboard() {
  const { user, isAdmin, settings } = useAuth();
  const { confirm } = useConfirm();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [rewards, setRewards] = useState([]);
  const cur = settings?.currency || "KMF";

  const loadRewards = () => api.get("/loyalty/rewards-due").then((r) => setRewards(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    loadRewards();
  }, []);

  const notify = (c) => {
    const phone = (c.phone || "").replace(/\D/g, "");
    if (!phone) return toast.error("Ce client n'a pas de numéro de téléphone.");
    openWhatsApp(phone, buildLoyaltyRewardMessage(c, settings));
  };

  const redeemReward = async (c) => {
    if (!(await confirm({ title: "Récompense remise", description: `Marquer une récompense de ${c.name} comme remise ?`, confirmText: "Confirmer" }))) return;
    try {
      await api.post(`/customers/${c.id}/loyalty/redeem`);
      toast.success("Récompense remise");
      loadRewards();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Bonjour, {user?.name} 👋</h1>
        <p className="text-slate-500">Voici l'activité du jour.</p>
      </div>

      {d && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI testid="kpi-revenue-today" label="CA du jour" value={fmtMoney(d.revenue_today, cur)} icon={Banknote} color="bg-sky-600" />
          <KPI testid="kpi-orders-today" label="Commandes du jour" value={d.orders_today} icon={ShoppingBag} color="bg-indigo-600" />
          <KPI testid="kpi-kg-today" label="Kg du jour" value={`${d.kg_today} kg`} icon={Scale} color="bg-violet-600" />
          <KPI testid="kpi-collected-today" label="Encaissé aujourd'hui" value={fmtMoney(d.paid_today, cur)} icon={Wallet} color="bg-emerald-600" />
          <KPI testid="kpi-pending" label="Commandes en cours" value={d.pending_orders} icon={Clock} color="bg-amber-500" />
          <KPI testid="kpi-ready" label="Commandes prêtes" value={d.ready_orders} icon={CheckCircle2} color="bg-teal-600" />
          <KPI testid="kpi-remaining" label="Reste à encaisser" value={fmtMoney(d.remaining_total, cur)} icon={AlertCircle} color="bg-rose-600" />
        </div>
      )}

      {(() => {
        const active = rewards.length > 0;
        return (
        <Card data-testid="rewards-due-card" className={`p-5 transition-colors ${active ? "border-amber-300 bg-amber-50/70 shadow-sm shadow-amber-200/40" : "border-slate-200 bg-slate-50/60"}`}>
          <div className="flex items-center gap-2 mb-3">
            <div className={`h-8 w-8 rounded-lg grid place-items-center ${active ? "bg-amber-500" : "bg-slate-300"}`}><Gift size={18} className="text-white" /></div>
            <h2 className="text-lg font-semibold text-slate-800">Récompenses à remettre <span data-testid="rewards-count" className={active ? "text-amber-600" : "text-slate-400"}>({rewards.length})</span></h2>
            {active && <span className="ml-1 inline-flex h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse" />}
          </div>
          {active ? (
            <>
              <p className="text-sm text-slate-500 mb-3">Ces clients fidèles ont une récompense disponible. Prévenez-les puis marquez-la comme remise.</p>
              <div className="space-y-2">
                {rewards.map((c) => (
                  <div key={c.id} data-testid={`reward-row-${c.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white rounded-lg border border-amber-100 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">{c.name} {c.rewards > 1 && <span className="text-xs text-amber-600 font-semibold">×{c.rewards}</span>}</p>
                      <p className="text-xs text-slate-500 truncate">🎁 {c.reward_label}{c.phone ? ` · ${c.phone}` : ""}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" data-testid={`notify-reward-${c.id}`} onClick={() => notify(c)} className="text-emerald-600 border-emerald-200"><MessageCircle size={14} className="mr-1" /> Notifier</Button>
                      <Button size="sm" data-testid={`use-reward-${c.id}`} onClick={() => redeemReward(c)} className="bg-amber-500 hover:bg-amber-600"><Gift size={14} className="mr-1" /> Utiliser</Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-400" data-testid="rewards-empty">Aucune récompense fidélité en attente pour le moment. Les clients ayant atteint leur palier apparaîtront ici automatiquement.</p>
          )}
        </Card>
        );
      })()}

      <div>
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Actions rapides</h2>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="quick-new-order" onClick={() => nav("/orders/new")} className="bg-sky-600 hover:bg-sky-700">
            <Plus size={18} className="mr-2" /> Nouvelle commande
          </Button>
          <Button data-testid="quick-customers" variant="outline" onClick={() => nav("/customers")}>
            <Users size={18} className="mr-2" /> Clients
          </Button>
          <Button data-testid="quick-orders" variant="outline" onClick={() => nav("/orders")}>
            <ShoppingBag size={18} className="mr-2" /> Commandes
          </Button>
          <Button data-testid="quick-cash" variant="outline" onClick={() => nav("/cash")}>
            <Wallet size={18} className="mr-2" /> Clôture de caisse
          </Button>
        </div>
      </div>
    </div>
  );
}
